<template>
  <div class="kpi-principal-container">
    <link href="https://api.mapbox.com/mapbox-gl-js/v3.1.2/mapbox-gl.css" rel="stylesheet">
    <!-- En-tête -->
    <div class="page-header">
      <div class="header-content">
        <h1 class="page-title">
          <i class="el-icon-data-analysis" />
          KPIs Principaux
        </h1>
        <div class="header-actions">
          <el-button
            type="primary"
            icon="el-icon-refresh"
            size="medium"
            :loading="loading"
            @click="refreshData"
          >
            Actualiser
          </el-button>
          <el-select
            v-model="camp_id"
            placeholder="Campagne Agricole"
            @change="setCampId(camp_id)"
          >
            <el-option
              v-for="item in campagnes_agricoles"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
          <span class="last-update">
            Dernière mise à jour: {{ lastUpdate }}
          </span>
        </div>
      </div>
    </div>

    <!-- Zone de contenu principal -->
    <div class="page-content">
      <!-- Message de chargement -->
      <div v-show="loading" class="loading-container">
        <i class="el-icon-loading" />
        <p>{{ loadingMessage }}</p>
      </div>

      <!-- Contenu principal -->
      <div v-show="!loading" class="main-content">
        <!-- KPIs Panel -->
        <el-card class="kpis-card" shadow="hover">
          <div slot="header" class="card-header">
            <span class="card-title">📊 Indicateurs clés</span>
          </div>
          <KpiPanel
            ref="kpiPanel"
            :parcelles="parcelles"
            :parcelles-geolocalisees="parcellesGeolocalisees"
            :parcelles-producteurs="parcellesProducteurs"
            :controleurs="controleurs"
            :producteurs="producteurs"
            :groupements="groupements"
            :unites-productions="unitesProductions"
            :audits="audits"
            :controles-internes="controlesInternes"
            :formations="formations"
            :surface-certifiee="surfaceCertifiee"
            :surface-declassee="surfaceDeclassee"
            :quantite-collectee="quantiteCollectee"
            @refresh="handleFiltersRefresh"
          />
        </el-card>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Resource from '@/api/resource';
import KpiPanel from './components/KpiPanel.vue';

const campagnesResource = new Resource('campagne_agricoles');
const parcelleResource = new Resource('parcelles');
const controleurResource = new Resource('controleurs');
const uniteProductionResource = new Resource('unite_productions');
const groupementResource = new Resource('groupements');
const producteurResource = new Resource('producteurs');
const produitResource = new Resource('produits');
const parcelleProduitResource = new Resource('parcelles');
const auditResource = new Resource('audits');
const controleInterneResource = new Resource('controle_internes');
const formationResource = new Resource('formations');

export default {
  name: 'KpiPrincipal',
  components: {
    KpiPanel,
  },
  data() {
    return {
      loading: false,
      lastUpdate: null,
      campagnes_agricoles: [],
      camp_id: null,
      camp_label: null,
      parcelles: [],
      parcellesGeolocalisees: [],
      parcellesProducteurs: [],
      controleurs: [],
      producteurs: [],
      groupements: [],
      unitesProductions: [],
      cultures: [],
      parcellesProduits: [],
      audits: [],
      controlesInternes: [],
      formations: [],
      surfaceCertifiee: 0,
      surfaceDeclassee: 0,
      quantiteCollectee: 0,
      loadingMessage: 'Chargement des données...',
    };
  },
  computed: {
    ...mapGetters('dashboard', ['filters', 'activeFilters']),
  },
  async created() {
    await this.campEnCours();
    await this.loadInitialLists();
    await this.getCampagnesList();
  },
  methods: {
    ...mapActions('dashboard', ['loadFilterOptions']),
    async loadInitialLists() {
      this.loading = true;
      try {
        await this.getParcellesProducteursList();
        await this.getProducteursList();
        await this.getGroupementsList();
        await this.getCulturesList();
        await this.getParcellesProduitsList();
        await this.getParcellesList();
        await this.getUnitesProductionsList();
        await this.getControleursList();
        await this.getAuditsList();
        await this.getControlesInternesList();
        await this.getFormationsList();
        await this.getSurfaceCertifiee();
        await this.getSurfaceDeclassee();
        await this.getQuantiteCollectee();
      } catch (error) {
        console.error('Erreur dans loadInitialLists:', error);
      } finally {
        this.loading = false;
        this.updateLastUpdate();
      }
    },
    async getParcellesList() {
      this.loadingMessage = 'Chargement des parcelles...';
      try {
        const params = { campagne_agricole_id: this.camp_id, campagne_id: this.camp_id };
        const response = await parcelleResource.sublist('/liste-simple', params);
        this.parcelles = response.data || response || [];

        // Enrichir chaque parcelle avec les données nécessaires
        if (
          Array.isArray(this.parcellesProducteurs) && this.parcellesProducteurs.length > 0 &&
          Array.isArray(this.producteurs) && this.producteurs.length > 0 &&
          Array.isArray(this.groupements) && this.groupements.length > 0 &&
          Array.isArray(this.parcellesProduits) && this.parcellesProduits.length > 0 &&
          Array.isArray(this.cultures) && this.cultures.length > 0 &&
          Array.isArray(this.parcelles) && this.parcelles.length > 0
        ) {
          const producteurById = {};
          this.producteurs.forEach(p => {
            if (p && p.id != null) {
              producteurById[p.id] = p;
            }
          });

          const groupementById = {};
          this.groupements.forEach(g => {
            if (g && g.id != null) {
              groupementById[g.id] = g;
            }
          });

          const produitById = {};
          this.cultures.forEach(prod => {
            if (prod && prod.id != null) {
              produitById[prod.id] = prod;
            }
          });

          const producteurIdByParcelleId = {};
          this.parcellesProducteurs.forEach(pp => {
            if (pp && pp.parcelle_id != null && pp.producteur_id != null) {
              producteurIdByParcelleId[pp.parcelle_id] = pp.producteur_id;
            }
          });

          const produitIdsByParcelleId = {};
          this.parcellesProduits.forEach(pp => {
            if (pp && pp.parcelle_id != null && pp.produit_id != null) {
              if (!produitIdsByParcelleId[pp.parcelle_id]) {
                produitIdsByParcelleId[pp.parcelle_id] = [];
              }
              produitIdsByParcelleId[pp.parcelle_id].push(pp.produit_id);
            }
          });

          this.parcelles = this.parcelles.map(parcelle => {
            const parcelleId = parcelle.id;
            const producteurId = producteurIdByParcelleId[parcelleId];
            if (!producteurId) {
              return parcelle;
            }
            const prod = producteurById[producteurId];
            if (!prod) {
              return parcelle;
            }
            const nom = prod.nom || '';
            const prenom = prod.prenom || '';
            const nomProducteur = [nom, prenom].filter(Boolean).join(' ');

            let groupementNom = parcelle.groupement_nom || parcelle.groupement || null;
            if (prod.groupement_id != null) {
              const grp = groupementById[prod.groupement_id];
              if (grp && grp.nom) {
                groupementNom = grp.nom;
              }
            }

            let cultures = parcelle.cultures || parcelle.produits || null;
            const produitIds = produitIdsByParcelleId[parcelleId];
            if (Array.isArray(produitIds) && produitIds.length > 0) {
              const nomsCultures = produitIds
                .map(id => (produitById[id] && produitById[id].nom) || null)
                .filter(Boolean);
              if (nomsCultures.length > 0) {
                cultures = nomsCultures;
              }
            }

            return {
              ...parcelle,
              nom_producteur: nomProducteur,
              groupement_nom: groupementNom,
              cultures,
            };
          });
        }

        this.parcellesGeolocalisees = this.parcelles.filter(parcelle => {
          const gps = parcelle.gps;
          return gps && gps !== null && gps !== undefined && gps !== '' && gps.trim() !== '';
        });
      } catch (error) {
        console.error('Erreur lors du chargement des parcelles:', error);
        this.parcelles = [];
      }
    },
    async getParcellesProducteursList() {
      this.loadingMessage = 'Chargement des parcelles/producteurs...';
      try {
        const response = await parcelleResource.sublist('/parcelle-producteurs-campagne-en-cours');
        this.parcellesProducteurs = response.data || response || [];
      } catch (error) {
        console.error('Erreur lors du chargement des parcelles_producteur:', error);
        this.parcellesProducteurs = [];
      }
    },
    async getControleursList() {
      this.loadingMessage = 'Chargement des contrôleurs...';
      try {
        const response = await controleurResource.sublist('/liste-simple');
        this.controleurs = response.data || response || [];

        if (
          Array.isArray(this.controleurs) && this.controleurs.length > 0 &&
          Array.isArray(this.unitesProductions) && this.unitesProductions.length > 0
        ) {
          const unitesByControleurId = {};
          const uniteIdsByControleurId = {};
          const controleurIdsInUnites = new Set();

          this.unitesProductions.forEach(up => {
            if (up && up.controleur_id != null) {
              controleurIdsInUnites.add(Number(up.controleur_id));
            }
          });

          this.unitesProductions.forEach(up => {
            if (up && up.controleur_id != null) {
              const normalizedControleurId = Number(up.controleur_id);
              if (isNaN(normalizedControleurId)) {
                return;
              }
              const label = up.nom || up.libelle || up.label || up.code || null;
              if (!label) {
                return;
              }
              if (!unitesByControleurId[normalizedControleurId]) {
                unitesByControleurId[normalizedControleurId] = [];
                uniteIdsByControleurId[normalizedControleurId] = [];
              }
              if (!unitesByControleurId[normalizedControleurId].includes(label)) {
                unitesByControleurId[normalizedControleurId].push(label);
              }
              const normalizedUpId = Number(up.id);
              if (!isNaN(normalizedUpId) && !uniteIdsByControleurId[normalizedControleurId].includes(normalizedUpId)) {
                uniteIdsByControleurId[normalizedControleurId].push(normalizedUpId);
              }
            }
          });

          const groupementsByUniteId = {};
          const groupementsByControleurId = {};
          if (Array.isArray(this.groupements) && this.groupements.length > 0) {
            this.groupements.forEach(g => {
              const uniteProdId = g.unite_production_id || g.unite_production || g.up_id || null;
              if (g && uniteProdId != null) {
                const normalizedId = Number(uniteProdId);
                if (!isNaN(normalizedId)) {
                  if (!groupementsByUniteId[normalizedId]) {
                    groupementsByUniteId[normalizedId] = [];
                  }
                  groupementsByUniteId[normalizedId].push(g);
                }
              }
              const controleurId = g.controleur_id || null;
              if (g && controleurId != null) {
                const normalizedControleurId = Number(controleurId);
                if (!isNaN(normalizedControleurId)) {
                  if (!groupementsByControleurId[normalizedControleurId]) {
                    groupementsByControleurId[normalizedControleurId] = [];
                  }
                  groupementsByControleurId[normalizedControleurId].push(g);
                }
              }
            });
          }

          const producteursByGroupementId = {};
          if (Array.isArray(this.producteurs) && this.producteurs.length > 0) {
            this.producteurs.forEach(p => {
              const groupementId = p.groupement_id || null;
              if (p && groupementId != null) {
                const normalizedGroupementId = Number(groupementId);
                if (!isNaN(normalizedGroupementId)) {
                  if (!producteursByGroupementId[normalizedGroupementId]) {
                    producteursByGroupementId[normalizedGroupementId] = [];
                  }
                  producteursByGroupementId[normalizedGroupementId].push(p);
                }
              }
            });
          }

          this.controleurs = this.controleurs.map(ctrl => {
            const normalizedControleurId = Number(ctrl.id);
            const unites = (!isNaN(normalizedControleurId) && unitesByControleurId[normalizedControleurId]) || ctrl.unite_production || [];
            const unitesArray = Array.isArray(unites) ? unites : (unites ? [unites] : []);

            const groupementsSet = new Set();
            const uniteIds = (!isNaN(normalizedControleurId) && uniteIdsByControleurId[normalizedControleurId]) || [];

            if (uniteIds.length > 0) {
              uniteIds.forEach(uniteId => {
                const normalizedId = Number(uniteId);
                if (!isNaN(normalizedId) && groupementsByUniteId[normalizedId]) {
                  groupementsByUniteId[normalizedId].forEach(g => {
                    groupementsSet.add(g.id);
                  });
                }
              });
            }

            if (!isNaN(normalizedControleurId) && groupementsByControleurId[normalizedControleurId]) {
              groupementsByControleurId[normalizedControleurId].forEach(g => {
                groupementsSet.add(g.id);
              });
            }

            const totalGroupements = groupementsSet.size;

            const producteursSet = new Set();
            const groupementIds = Array.from(groupementsSet);
            groupementIds.forEach(groupementId => {
              const normalizedGroupementId = Number(groupementId);
              if (!isNaN(normalizedGroupementId) && producteursByGroupementId[normalizedGroupementId]) {
                producteursByGroupementId[normalizedGroupementId].forEach(p => {
                  producteursSet.add(p.id);
                });
              }
            });

            const totalProducteurs = producteursSet.size;

            return {
              ...ctrl,
              unite_production: unitesArray,
              groupements: totalGroupements,
              producteurs: totalProducteurs,
            };
          });
        }
      } catch (error) {
        console.error('Erreur lors du chargement des contrôleurs:', error);
        this.controleurs = [];
      }
    },
    async getUnitesProductionsList() {
      this.loadingMessage = 'Chargement des unités de production...';
      try {
        const params = { campagne_id: this.camp_id, campagne_agricole_id: this.camp_id };
        const response = await uniteProductionResource.sublist('/liste-simple', params);
        this.unitesProductions = response.data || response || [];
      } catch (error) {
        console.error('Erreur lors du chargement des unités de production:', error);
        this.unitesProductions = [];
      }
    },
    async getGroupementsList() {
      this.loadingMessage = 'Chargement des groupements...';
      try {
        const params = { campagne_id: this.camp_id, campagne_agricole_id: this.camp_id };
        const response = await groupementResource.sublist('/liste-simple', params);
        this.groupements = response.data || response || [];
      } catch (error) {
        console.error('Erreur lors du chargement des groupements:', error);
        this.groupements = [];
      }
    },
    async getCulturesList() {
      this.loadingMessage = 'Chargement des cultures...';
      try {
        const params = { campagne_id: this.camp_id, campagne_agricole_id: this.camp_id };
        const response = await produitResource.sublist('/liste-simple', params);
        this.cultures = response.data || response || [];
      } catch (error) {
        console.error('Erreur lors du chargement des cultures:', error);
        this.cultures = [];
      }
    },
    async getProducteursList() {
      this.loadingMessage = 'Chargement des producteurs...';
      try {
        const params = { campagne_id: this.camp_id, campagne_agricole_id: this.camp_id };
        const response = await producteurResource.sublist('/liste-simple', params);
        this.producteurs = response.data || response || [];
      } catch (error) {
        console.error('Erreur lors du chargement des producteurs:', error);
        this.producteurs = [];
      }
    },
    async getParcellesProduitsList() {
      this.loadingMessage = 'Chargement des parcelles-produits...';
      try {
        const params = {
          campagne_agricole_id: this.camp_id,
        };
        const response = await parcelleProduitResource.sublist('/parcelle-produits-campagne-en-cours', params);
        this.parcellesProduits = response.data || response || [];
      } catch (error) {
        console.error('Erreur lors du chargement des parcelles-produits:', error);
        this.parcellesProduits = [];
      }
    },
    async getAuditsList() {
      this.loadingMessage = 'Chargement des audits...';
      try {
        const params = { campagne_id: this.camp_id, campagne_agricole_id: this.camp_id };
        const response = await auditResource.sublist('/liste-simple', params);
        this.audits = response.data || response || [];
      } catch (error) {
        console.error('Erreur lors du chargement des audits:', error);
        this.audits = [];
      }
    },
    async getControlesInternesList() {
      this.loadingMessage = 'Chargement des contrôles internes...';
      try {
        const params = { campagne_id: this.camp_id, campagne_agricole_id: this.camp_id };
        const response = await controleInterneResource.sublist('/liste-simple', params);
        this.controlesInternes = response.data || response || [];
      } catch (error) {
        console.error('Erreur lors du chargement des contrôles internes:', error);
        this.controlesInternes = [];
      }
    },
    async getFormationsList() {
      this.loadingMessage = 'Chargement des formations...';
      try {
        const params = { campagne_id: this.camp_id, campagne_agricole_id: this.camp_id };
        const response = await formationResource.sublist('/liste-simple', params);
        this.formations = response.data || response || [];
      } catch (error) {
        console.error('Erreur lors du chargement des formations:', error);
        this.formations = [];
      }
    },
    async getSurfaceCertifiee() {
      this.loadingMessage = 'Chargement de la surface certifiée...';
      try {
        const params = { campagne_id: this.camp_id, campagne_agricole_id: this.camp_id };
        const response = await parcelleResource.sublist('/surface-certifiee', params);
        if (response.data && response.data.data) {
          this.surfaceCertifiee = response.data.data.surface_certifiee || 0;
        } else if (response.data && response.data.surface_certifiee !== undefined) {
          this.surfaceCertifiee = response.data.surface_certifiee || 0;
        } else if (response.data && response.data.data && response.data.data.surface_certifiee !== undefined) {
          this.surfaceCertifiee = response.data.data.surface_certifiee || 0;
        }
      } catch (error) {
        console.error('Erreur lors du chargement de la surface certifiée:', error);
        this.surfaceCertifiee = 0;
      }
    },
    async getSurfaceDeclassee() {
      this.loadingMessage = 'Chargement de la surface déclassée...';
      try {
        const params = { campagne_id: this.camp_id, campagne_agricole_id: this.camp_id };
        const response = await parcelleResource.sublist('/surface-declassee', params);
        if (response.data && response.data.data) {
          this.surfaceDeclassee = response.data.data.surface_declassee || 0;
        } else if (response.data && response.data.surface_declassee !== undefined) {
          this.surfaceDeclassee = response.data.surface_declassee || 0;
        } else if (response.data && response.data.data && response.data.data.surface_declassee !== undefined) {
          this.surfaceDeclassee = response.data.data.surface_declassee || 0;
        }
      } catch (error) {
        console.error('Erreur lors du chargement de la surface déclassée:', error);
        this.surfaceDeclassee = 0;
      }
    },
    async getQuantiteCollectee() {
      this.loadingMessage = 'Chargement de la quantité collectée...';
      try {
        const params = { campagne_id: this.camp_id, campagne_agricole_id: this.camp_id };
        const response = await parcelleResource.sublist('/quantite-collectee', params);
        this.quantiteCollectee = response.data.quantite_collectee_tones;
      } catch (error) {
        console.error('Erreur lors du chargement de la quantité collectée:', error);
        this.quantiteCollectee = 0;
      }
    },
    handleFiltersRefresh() {
      this.loadInitialLists();
    },
    async refreshData() {
      this.loading = true;
      await this.loadInitialLists();
    },
    updateLastUpdate() {
      const now = new Date();
      this.lastUpdate = now.toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    async getCampagnesList() {
      const { data } = await campagnesResource.list();
      this.campagnes_agricoles = [];
      data.forEach((camp) => {
        this.campagnes_agricoles.push({ value: camp.id, label: camp.periode });
      });
    },
    async campEnCours() {
      const { data } = await campagnesResource.sublist('/en_cours');
      this.camp_id = data.id;
      this.camp_label = data.periode;
      sessionStorage.setItem('camp_id', this.camp_id);
      sessionStorage.setItem('camp_label', this.camp_label);
      await this.$store.dispatch('camp_agricole/set_camp', this.camp_id);
      await this.$store.dispatch('camp_agricole/set_camp_label', this.camp_label);
    },
    async setCampId(id) {
      await this.$store.dispatch('camp_agricole/set_camp', id);
      const found = this.campagnes_agricoles.find(c => c.value === id);
      if (found && found.label) {
        await this.$store.dispatch('camp_agricole/set_camp_label', found.label);
      }
      sessionStorage.setItem('camp_id', id);
      sessionStorage.setItem('camp_label', found.label);
      this.camp_id = id;
      this.camp_label = found.label;
      await this.loadInitialLists();
    },
  },
};
</script>

<style lang="scss" scoped>
.kpi-principal-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 20px;
}

.page-header {
  background: linear-gradient(135deg, #00ab53 0%, #00d977 100%);
  border-radius: 12px;
  padding: 24px 32px;
  margin-bottom: 24px;
  box-shadow: 0 4px 12px rgba(0, 171, 83, 0.2);

  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
  }

  .page-title {
    color: white;
    font-size: 28px;
    font-weight: 600;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 12px;

    i {
      font-size: 32px;
    }
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 20px;

    .el-button {
      background: rgba(255, 255, 255, 0.2);
      border-color: rgba(255, 255, 255, 0.5);
      color: white;
      font-weight: 500;
      padding: 10px 20px;
      border-radius: 6px;
      transition: all 0.3s ease;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

      &:hover {
        background: rgba(255, 255, 255, 0.3);
        border-color: white;
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      }
    }

    .el-select {
      min-width: 200px;
    }

    .last-update {
      color: rgba(255, 255, 255, 0.9);
      font-size: 14px;
    }
  }
}

.page-content {
  .loading-container {
    text-align: center;
    padding: 60px 20px;
    color: #606266;

    i {
      font-size: 48px;
      color: #00ab53;
      animation: rotate 1s linear infinite;
    }

    p {
      margin-top: 16px;
      font-size: 16px;
    }
  }

  .main-content {
    .kpis-card {
      border-radius: 8px;
      margin-bottom: 20px;
      border: 1px solid #e4e7ed;
      transition: all 0.3s ease;

      &:hover {
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
        transform: translateY(-2px);
      }

      .card-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0;

        .card-title {
          font-size: 18px;
          font-weight: 600;
          color: #303133;
        }
      }
    }
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 768px) {
  .page-header {
    padding: 16px 20px;

    .header-content {
      flex-direction: column;
      align-items: flex-start;
      gap: 16px;
    }

    .page-title {
      font-size: 22px;
    }

    .header-actions {
      width: 100%;
      flex-direction: column;
      align-items: flex-start;
    }
  }

  .kpi-principal-container {
    padding: 12px;
  }
}
</style>

